/**
 * Small persisted UI preferences (favorites, recently used plants, display units, panel state).
 *
 * These are *view* preferences, not project data — they deliberately live in `localStorage` rather
 * than the `LandscapeProject` document, so opening someone else's project does not overwrite your
 * favorites. Storage failures (private mode, disabled cookies) degrade to in-memory only.
 */

export type DisplayUnit = 'meters' | 'feet';

/** How much a scene pointer says. Mirrors `PointerDetail`, kept as a string in storage. */
export type PointerDetailPreference = 'tag' | 'name' | 'full';

/**
 * A palette in the left dock. Three stacked panels — one per placeable class — each of which shuts
 * on its own. See `docs/PLACEABLE_CLASSES.md`.
 */
export type PalettePanel = 'flora' | 'fauna' | 'objects';

export const PALETTE_PANELS: readonly PalettePanel[] = ['flora', 'fauna', 'objects'];

interface PreferencesData {
  favorites: string[];
  recent: string[];
  unit: DisplayUnit;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  /**
   * Which left-dock palettes are shut, by {@link PalettePanel}.
   *
   * A list rather than three booleans so adding a fourth class does not migrate the stored shape,
   * and so an unrecognised entry written by a newer build is simply ignored rather than fatal.
   *
   * Fauna and objects start shut. Flora is what almost every session opens to do, and three
   * expanded palettes would give each of them a third of the column — the wrong default for the one
   * people actually browse.
   */
  shutPanels: string[];
  /**
   * Whether the scene pointers are lit, and what they say.
   *
   * A view aid, so it belongs here rather than in the document for the reason above: which labels
   * you like reading is a fact about you, not about the garden, and opening someone else's site
   * should not turn them on or off.
   */
  pointers: boolean;
  pointerDetail: PointerDetailPreference;
  /** Whether buildings, paving, and props are pointed at along with the planting. */
  pointerObjects: boolean;
  /** Point only at what is selected — the way to read one bed in a crowded site. */
  pointerSelectionOnly: boolean;
  /**
   * The site lens the design view is reading through, or `null` for none. A {@link LensId}, stored
   * loosely as a string so a lens retired in a later build is ignored rather than fatal.
   *
   * Here rather than in the document for the same reason the pointers are: which question you are
   * looking at the garden through is a fact about you, and opening someone else's site should not
   * switch their view to bird food because that is what you were last reading.
   */
  lens: string | null;
  /** Read each plant at its best day rather than at the date on the chrono bar. */
  lensPeak: boolean;
}

const STORAGE_KEY = 'landscape-glade.ui';
const MAX_RECENT = 8;

function defaults(): PreferencesData {
  return {
    favorites: [],
    recent: [],
    unit: 'meters',
    leftCollapsed: false,
    rightCollapsed: false,
    shutPanels: ['fauna', 'objects'],
    pointers: false,
    pointerDetail: 'name',
    pointerObjects: true,
    pointerSelectionOnly: false,
    lens: null,
    lensPeak: false,
  };
}

const POINTER_DETAILS: readonly PointerDetailPreference[] = ['tag', 'name', 'full'];

function readStorage(): PreferencesData {
  const base = defaults();
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<PreferencesData>;
    return {
      favorites: Array.isArray(parsed.favorites) ? parsed.favorites.filter((v) => typeof v === 'string') : [],
      recent: Array.isArray(parsed.recent) ? parsed.recent.filter((v) => typeof v === 'string') : [],
      unit: parsed.unit === 'feet' ? 'feet' : 'meters',
      leftCollapsed: parsed.leftCollapsed === true,
      rightCollapsed: parsed.rightCollapsed === true,
      // An absent key means "never chosen", which takes the default; an empty array means the user
      // opened all three, and must not be read as absent.
      shutPanels: Array.isArray(parsed.shutPanels)
        ? parsed.shutPanels.filter((v): v is string => typeof v === 'string')
        : ['fauna', 'objects'],
      pointers: parsed.pointers === true,
      pointerDetail: POINTER_DETAILS.includes(parsed.pointerDetail as PointerDetailPreference)
        ? (parsed.pointerDetail as PointerDetailPreference)
        : 'name',
      // Defaults to on, so an absent key has to be distinguished from an explicit `false`.
      pointerObjects: parsed.pointerObjects !== false,
      pointerSelectionOnly: parsed.pointerSelectionOnly === true,
      lens: typeof parsed.lens === 'string' ? parsed.lens : null,
      lensPeak: parsed.lensPeak === true,
    };
  } catch {
    return base;
  }
}

export class UiPreferences {
  private data: PreferencesData;
  private readonly listeners = new Set<() => void>();

  constructor() {
    this.data = readStorage();
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get favorites(): ReadonlySet<string> {
    return new Set(this.data.favorites);
  }

  isFavorite(id: string): boolean {
    return this.data.favorites.includes(id);
  }

  toggleFavorite(id: string): void {
    this.data.favorites = this.isFavorite(id)
      ? this.data.favorites.filter((v) => v !== id)
      : [...this.data.favorites, id];
    this.commit();
  }

  /** Most-recently-used plant ids, newest first. */
  get recent(): readonly string[] {
    return this.data.recent;
  }

  noteUsed(id: string): void {
    this.data.recent = [id, ...this.data.recent.filter((v) => v !== id)].slice(0, MAX_RECENT);
    this.commit();
  }

  get unit(): DisplayUnit {
    return this.data.unit;
  }

  setUnit(unit: DisplayUnit): void {
    this.data.unit = unit;
    this.commit();
  }

  /* --------------------------------- Scene pointers --------------------------------- */

  get pointers(): boolean {
    return this.data.pointers;
  }

  setPointers(visible: boolean): void {
    this.data.pointers = visible;
    this.commit();
  }

  get pointerDetail(): PointerDetailPreference {
    return this.data.pointerDetail;
  }

  setPointerDetail(detail: PointerDetailPreference): void {
    this.data.pointerDetail = detail;
    this.commit();
  }

  get pointerObjects(): boolean {
    return this.data.pointerObjects;
  }

  setPointerObjects(include: boolean): void {
    this.data.pointerObjects = include;
    this.commit();
  }

  get pointerSelectionOnly(): boolean {
    return this.data.pointerSelectionOnly;
  }

  setPointerSelectionOnly(only: boolean): void {
    this.data.pointerSelectionOnly = only;
    this.commit();
  }

  /** The lens the design view is reading through, or null for none. See `FloraLens.ts`. */
  get lens(): string | null {
    return this.data.lens;
  }

  setLens(lens: string | null): void {
    this.data.lens = lens;
    this.commit();
  }

  get lensPeak(): boolean {
    return this.data.lensPeak;
  }

  setLensPeak(peak: boolean): void {
    this.data.lensPeak = peak;
    this.commit();
  }

  /** Whether a left-dock palette is shut — collapsed to its header row. */
  panelShut(panel: PalettePanel): boolean {
    return this.data.shutPanels.includes(panel);
  }

  setPanelShut(panel: PalettePanel, shut: boolean): void {
    const without = this.data.shutPanels.filter((entry) => entry !== panel);
    this.data.shutPanels = shut ? [...without, panel] : without;
    this.commit();
  }

  collapsed(side: 'left' | 'right'): boolean {
    return side === 'left' ? this.data.leftCollapsed : this.data.rightCollapsed;
  }

  setCollapsed(side: 'left' | 'right', value: boolean): void {
    if (side === 'left') this.data.leftCollapsed = value;
    else this.data.rightCollapsed = value;
    this.commit();
  }

  private commit(): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch {
      // Non-fatal: preferences simply do not persist this session.
    }
    for (const listener of this.listeners) listener();
  }
}
